import DateFormat from '@/Enums/Format/DateFormat.enum';
import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import { CapturePDFButton } from '@/components/Buttons/CapturePDFButton';
import { DownloadButton } from '@/components/Buttons/DownloadButton';
import { AVG_IMPRESSION_CSV } from '@/constants/attribution';
import { PRIMARY } from '@/constants/color';
import { downloadCSV } from '@/helpers/downloadCSV';
import { getDocumentTitle } from '@/helpers/printing';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { useAppSelector } from '@/store/hooks';
import { selectWebVisitsDateData } from '@/store/selectors/attribution/webVisits.selectors';
import { CircularProgress, Skeleton } from '@mui/material';
import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import { useReactToPrint } from 'react-to-print';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend, LineController);

interface CustomChartOptions extends ChartOptions<'bar'> {
  maxBarThickness?: number;
  borderRadius?: number;
  borderSkipped?: boolean;
}

const legendOptions = {
  impressions: {
    label: 'Impressions',
    color: PRIMARY,
  },
  conversion: {
    label: 'Conversion',
    color: 'green',
  },
  clicks: {
    label: 'Clicks',
    color: 'orange',
  },
};

const chartOptions: CustomChartOptions = {
  responsive: true,
  plugins: {
    datalabels: {
      display: false,
    },
    legend: {
      position: 'top',
      align: 'end',
      reverse: true,
      labels: {
        boxHeight: 12,
        boxWidth: 12,
        padding: 12,
      },
    },
  },
  maxBarThickness: 47,
  borderRadius: 5,
  borderSkipped: false,
  backgroundColor: '#193B2C',
  scales: {
    y: {
      title: {
        display: true,
        text: legendOptions.impressions.label,
        align: 'center',
        color: legendOptions.impressions.color,
      },
      grid: {
        drawTicks: false,
      },
      border: {
        display: false,
        dash: [6, 3],
      },
    },
    y1: {
      title: {
        display: true,
        text: legendOptions.conversion.label,
        align: 'center',
        color: legendOptions.conversion.color,
      },
      position: 'right',
      grid: {
        drawTicks: false,
        drawOnChartArea: false,
      },
    },
    y2: {
      title: {
        text: legendOptions.clicks.label,
        display: true,
        align: 'center',
        color: legendOptions.clicks.color,
      },
      position: 'right',
      grid: {
        drawTicks: false,
        drawOnChartArea: false,
      },
    },
    x: {
      ticks: {
        autoSkip: false,
        color: '#84818A',
      },
    },
  },
  maintainAspectRatio: false,
};

const ImpressionCard = () => {
  const printContainerRef = useRef<HTMLDivElement>(null);

  const { dateData, totals, dateDataLoading } = useAppSelector(selectWebVisitsDateData);

  const averageImpressions = totals[BeeswaxReportProperties.IMPRESSIONS] || 0;

  const [chartData, setChartData] = useState<ChartData>({
    labels: [],
    datasets: [
      {
        data: [],
      },
    ],
  });

  useEffect(() => {
    const data = dateData?.filter(val => val.Day);

    const dateObjects = data.map(dayData => {
      const [year, month, day] = dayData.Day.split('-').map(Number);
      return new Date(year, month - 1, day);
    });

    const sortedDates = dateObjects.map(date => {
      return moment(date).format(DateFormat.ShortMonthDay);
    });

    const impressions = data.map(dayData => dayData[BeeswaxReportProperties.IMPRESSIONS]);
    const clicks = data.map(dayData => dayData[BeeswaxReportProperties.CLICKS]);
    const conversions = data.map(dayData => dayData[BeeswaxReportProperties.IP_CONVERSIONS]);

    setChartData({
      labels: sortedDates,
      datasets: [
        {
          type: 'line',
          label: legendOptions.clicks.label,
          data: clicks,
          borderColor: legendOptions.clicks.color,
          backgroundColor: legendOptions.clicks.color,
          borderWidth: 3,
          fill: false,
          yAxisID: 'y2',
        },
        {
          type: 'line',
          label: legendOptions.conversion.label,
          data: conversions,
          borderColor: legendOptions.conversion.color,
          backgroundColor: legendOptions.conversion.color,
          borderWidth: 3,
          fill: false,
          yAxisID: 'y1',
        },
        {
          type: 'bar',
          label: legendOptions.impressions.label,
          data: impressions,
          backgroundColor: legendOptions.impressions.color,
          yAxisID: 'y',
        },
      ],
    });
  }, [dateData, setChartData]);

  const handleDownloadClick = useCallback(() => {
    const labels = chartData.labels as string[];
    const values = chartData.datasets[0].data as number[];
    const downloadCSVData = labels?.reduce<Record<string, number>>((acc, label, index) => {
      acc[label] = values[index];
      return acc;
    }, {});
    downloadCSV(downloadCSVData, AVG_IMPRESSION_CSV);
  }, [chartData]);

  const handlePrint = useReactToPrint({
    documentTitle: getDocumentTitle('Avg. Impression'),
    content: () => printContainerRef.current,
  });

  return (
    <div className='w-full print:p-6' ref={printContainerRef}>
      <div className='flex justify-between mb-[24px]'>
        <div className='flex flex-col gap-2'>
          <div className='text-lg text-[#2E2C34]'>Avg. Impression</div>
          <div className='flex items-center gap-2'>
            {dateDataLoading ? (
              <Skeleton width={100} height={36} variant='rounded' />
            ) : (
              <span className='text-3xl font-bold'>{numberToUsFormat(averageImpressions)}</span>
            )}
            <span className='text-sm text-[#2E2C34]'>in selected Period</span>
          </div>
        </div>
        <div className='flex gap-1'>
          <DownloadButton onClick={handleDownloadClick} disabled={dateDataLoading} />
          <CapturePDFButton onClick={handlePrint} disabled={dateDataLoading} />
        </div>
      </div>
      {dateDataLoading ? (
        <div className='flex w-full justify-center mt-16 mb-16'>
          <CircularProgress />
        </div>
      ) : (
        <div className='flex flex-col w-full'>
          <div className='bar_chart'>
            <Chart
              type='bar'
              style={{ height: 283, width: '100%' }}
              options={chartOptions}
              data={chartData as ChartData<'bar'>}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpressionCard;
